import { useCallback, useState } from "react";
import { useTranslation } from "@coworker/locales";
import SelectItemSheet from "@coworker/apprestructured/src/task-management/components/SelectItemSheet/SelectItemSheet";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import ErrorAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";

export type ListItem = {
  value: string;
  label: string;
};

type SelectListItemProps = {
  title: string;
  selectedItem: ListItem | null;
  items: ListItem[];
  onConfirmClick: (item: ListItem | null) => void;
  showError?: boolean;
  errorMessage?: string;
};

function SelectListItem({
  title,
  selectedItem,
  items,
  onConfirmClick,
  showError,
  errorMessage,
}: SelectListItemProps) {
  const [draftItemValue, setDraftItemValue] = useState("");
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  const getItemByValue = useCallback(
    (value: string) => items.find((item) => item.value === value) ?? null,
    [items]
  );

  const handleOpenClick = useCallback(() => {
    setDraftItemValue(selectedItem?.value ?? "");
    setVisible(true);
  }, [selectedItem]);

  const handleConfirmClick = useCallback(() => {
    onConfirmClick(getItemByValue(draftItemValue));
    setVisible(false);
  }, [draftItemValue, getItemByValue, onConfirmClick]);

  return (
    <>
      <FixaListViewItem
        title={t(title)}
        inset
        onClick={handleOpenClick}
        emphasised={!!selectedItem?.value}
        description={t(selectedItem?.label ?? "")}
        control="navigational"
        controlIcon={null}
        addOn={
          showError && errorMessage ? (
            <ErrorAddOn>{errorMessage}</ErrorAddOn>
          ) : null
        }
      />
      <SelectItemSheet
        title={t(title)}
        itemValue={draftItemValue}
        items={items.map((item) => ({
          value: item.value,
          label: t(item.label),
        }))}
        onItemValueChange={setDraftItemValue}
        onConfirmClick={handleConfirmClick}
        onClose={() => setVisible(false)}
        visible={visible}
      />
    </>
  );
}

export default SelectListItem;
